// GivingDay MenuBar

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { ClickAwayListener } from "@mui/material";

import { commaNumber } from "lib/utils/numberFunctions";

import onResize from "../../hoc/onResize";
import onScroll from "../../hoc/onScroll";

import renderStatType from "../renderStatType";

import Logo from "./Logo";
import SubNavLeft from "./SubNavLeft";
import SubNavRight from "./SubNavRight";
import Burger from "./Burger";
import MobileDropdown from "./MobileDropdown";
import GivingButton from "./GivingButton";
import GivingCountdown from "./GivingCountdown";
import SearchForm from "./SearchForm";
import { IntlProvider } from "react-intl";
import { fetchLocale } from "shared/helpers/localeHelper";

const NAV_STICKY_RELEASE_TOP_PX = 155;
const NAV_SCROLL_SHRINK_TOP_PX = -200;

const MenuBar = React.forwardRef((props, ref) => {
  const [state, setState] = useState({
    sticky: false,
    shrinkLogo: false,
    dropdownOpen: false,
  });

  const prevNavAnchorTopRef = useRef(null);

  const { sticky, shrinkLogo, dropdownOpen } = state;

  const handleBurger = (e) => {
    e.preventDefault();

    setState((prevState) => ({
      ...prevState,
      dropdownOpen: !prevState.dropdownOpen,
    }));
  };

  const handleClickOutside = () => {
    if (dropdownOpen) {
      setState((prevState) => ({
        ...prevState,
        dropdownOpen: false,
      }));
    }
  };

  useEffect(() => {
    const top = props.elementPosition?.top;
    const prevTop = prevNavAnchorTopRef.current;

    if (
      sticky &&
      top < NAV_SCROLL_SHRINK_TOP_PX &&
      (prevTop === null || prevTop > top)
    ) {
      setState((prevState) => ({
        ...prevState,
        shrinkLogo: true,
      }));
    } else if (
      sticky &&
      prevTop !== null &&
      prevTop < top &&
      top > NAV_SCROLL_SHRINK_TOP_PX
    ) {
      setState((prevState) => ({
        ...prevState,
        shrinkLogo: false,
      }));
    } else if (top >= NAV_STICKY_RELEASE_TOP_PX) {
      setState((prevState) => ({
        ...prevState,
        sticky: false,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        sticky: true,
      }));
    }

    if (top != null) {
      prevNavAnchorTopRef.current = top;
    }
  }, [props.elementPosition, sticky]);

  const isMobile = () => {
    const {
      elementPosition: { width },
    } = props;

    // 992 from giving-day-nav media query
    return width <= 992;
  };

  const splitLinks = (links) => {
    const sortedLinks = sortLinks(links);
    const middle = Math.floor(sortedLinks.length / 2);
    const left = sortedLinks.slice(0, middle);
    const right = sortedLinks.slice(middle);

    return {
      left,
      right,
    };
  };

  const sortLinks = (links) => {
    return links.sort((a, b) => {
      // if no position, sort to the end
      if (a.position == null) {
        return 1;
      }
      if (b.position == null) {
        return -1;
      }

      return a.position - b.position;
    });
  };

  const _className = (sticky, shrinkLogo) => {
    let _className = "";

    if (sticky && shrinkLogo) {
      _className = "giving-nav giving-nav-small fixed";
    } else {
      _className = "giving-nav fixed";
    }
    return _className;
  };

  const hideStats = () => {
    const {
      menu: { display_stats },
    } = props;

    return display_stats ? "" : " hidden";
  };

  const {
    menu,
    menu: {
      menu_links,
      main_logo,
      giving_button_text,
      giving_button_config,
      countdown,
      countdown_end,
      countdown_campaign,
      display_countdown,
      display_search,
      display_stats,
      stat_type,
    },
    website: { head_logo_link },
    entity: {
      external_give_link,
      host,
      microsite_campaign,
      features_enabled,
      name,
      currency_symbol,
      currency_code,
      language,
    },
    current_campaign,
    entity,
  } = props;

  const locale = fetchLocale(language);
  const campaign = current_campaign || countdown_campaign || microsite_campaign;
  const isCampaignPage = window.location.href.includes("/campaigns/");

  const offsetClassName = [
    "giving-nav-offset",
    display_stats && "giving-nav-offset--with-stats",
    display_search && "giving-nav-offset--with-search",
  ]
    .filter(Boolean)
    .join(" ");

  const { statText, statNumber } = renderStatType(campaign, stat_type);

  return (
    <IntlProvider locale={locale}>
      {campaign ? (
        <ClickAwayListener onClickAway={handleClickOutside}>
          <div className="giving-nav-root" ref={ref}>
            <header className={_className(sticky, shrinkLogo)}>
              <a
                data-testid="t-MenuBar-GivingD--MenuBar-a"
                href="#main-content"
                className="skip-link">
                Skip to main content
              </a>
              <div
                className={
                  dropdownOpen
                    ? "giving-nav-bottom-border clear-border primary-color-border"
                    : "giving-nav-bottom-border primary-color-border"
                }
              />
              <div className="giving-nav-logo-outline primary-color-border" />
              <div className="giving-nav-total-mobile giving-nav-mobile-display">
                <div className={`text-center${hideStats()}`}>
                  <div className="giving-nav-total">{statText}</div>
                  <div>
                    <strong>{statNumber}</strong>
                  </div>
                </div>
                <div className={`text-center${hideStats()}`}>
                  <div className="giving-nav-total primary-color-text">
                    Total dollars:
                  </div>
                  <div className="primary-color-text">
                    <strong>
                      {currency_symbol}
                      {commaNumber(campaign.amount_raised_including_family)}
                    </strong>
                  </div>
                </div>
              </div>

              <div className="giving-nav-container">
                <div className="giving-nav-wrap">
                  <SubNavLeft
                    className="giving-nav-sub-left"
                    links={splitLinks(menu_links).left}
                    countdown={countdown}
                    endCountdown={countdown_end}
                    display_countdown={display_countdown}
                  />
                  <Logo
                    src={main_logo}
                    sticky={sticky}
                    headLogoLink={head_logo_link}
                    alt={name}
                  />
                  <SubNavRight
                    className="giving-nav-sub-right"
                    givingButtonText={giving_button_text}
                    givingButtonConfig={giving_button_config}
                    externalGiveLink={external_give_link}
                    shrink={shrinkLogo}
                    links={splitLinks(menu_links).right}
                    countdown={countdown}
                    endCountdown={countdown_end}
                    display_stats={display_stats}
                    currency_code={currency_code}
                    statType={stat_type}
                    menu={menu}
                    host={host}
                    campaign={isCampaignPage ? props.minified_campaign : campaign}
                    entityId={entity.id}
                  />

                  <div className="giving-nav-mobile-display">
                    {display_search && <SearchForm host={host} />}

                    <GivingCountdown
                      countdown={countdown}
                      endCountdown={countdown_end}
                      display_countdown={display_countdown}
                    />

                    <div className="giving-nav-total-container">
                      <div className={`giving-nav-total-number--right${hideStats()}`}>
                        <div className="giving-nav-total total-amount">
                          {statText}:&nbsp;
                        </div>
                        <div className="total-amount">
                          <strong>{statNumber}</strong>
                        </div>
                      </div>

                      <div className={`giving-nav-total-number${hideStats()}`}>
                        <div className="giving-nav-total total-amount primary-color-text">
                          total dollars:&nbsp;
                        </div>
                        <div className="total-amount primary-color-text">
                          <strong>
                            {currency_symbol}
                            {commaNumber(
                              Math.floor(campaign.amount_raised_including_family),
                            )}
                          </strong>
                        </div>
                      </div>
                    </div>

                    <GivingButton
                      buttonText={giving_button_text}
                      buttonConfig={giving_button_config}
                      externalGiveLink={external_give_link}
                      entityFeaturesEnabled={features_enabled}
                      campaign={isCampaignPage ? props.minified_campaign : campaign}
                      entityId={entity.id}
                      countdown={countdown}
                      endCountdown={countdown_end}
                      fromMenuBar="true"
                    />
                    <Burger onClick={handleBurger} open={dropdownOpen} />
                  </div>
                </div>
              </div>
              <MobileDropdown
                open={dropdownOpen}
                links={menu_links}
                countdown={countdown}
                endCountdown={countdown_end}
              />
            </header>
            {/* offset to allow nav to change to a fixed position */}
            <div className={offsetClassName} />
          </div>
        </ClickAwayListener>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "20px 0",
            fontWeight: "bold",
            fontSize: "20px",
          }}>
          Error: select a campaign to display the menu bar
        </div>
      )}
    </IntlProvider>
  );
});

export { MenuBar };

MenuBar.displayName = "MenuBar";

MenuBar.propTypes = {
  website: PropTypes.object.isRequired,
  entity: PropTypes.object.isRequired,
  minified_campaign: PropTypes.object,
  menu: PropTypes.shape({
    menu_links: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
        external: PropTypes.bool,
      }),
    ),
    display_stats: PropTypes.bool,
    stat_type: PropTypes.string,
  }),
  elementPosition: PropTypes.shape({
    top: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    scrollTop: PropTypes.number,
  }),
};

export default onResize(onScroll(MenuBar));
